/**
 * useLiveData — app-level Zenoh live data overlay.
 *
 * Owns the single fetch-SSE connection for the active site (plan
 * §6.4: "one connection per active site") and applies incoming
 * events to the shared TanStack Query cache.
 *
 * Two categories of subscription live here, on two different paths:
 *
 * 1. `fleets` / `robots` / `doors` / `lifts` / `tasks` — the v1
 *    `CategoryName` categories. These go through
 *    `category-reducers.ts`'s `createSiteReducerRegistry` +
 *    `applyToRegistry`, which is the only code path that enforces
 *    the live consistency protocol (snapshot replace, manifest
 *    prune, tombstone delete, `needs_snapshot` → exact resync). The
 *    reducer's output is adapted with the *same* functions the REST
 *    seed in `rmf-client.ts` uses, then written into the identical
 *    `v1Key()`-based cache slot `query-options.ts` reads — the REST
 *    and stream paths must never write divergent representations
 *    (plan §6.2).
 *
 *    `building_map_revision` notifications (small, change-only) are
 *    not a reducer category; they just invalidate the assembled
 *    building-map query so `useBuildingMap` refetches the exact
 *    snapshot (plan §Map contract — map bytes are never broadcast on
 *    the broad live stream).
 *
 *    `task_distance` events (a task's distance and cleaning-time
 *    totals, worker 0.4.33) are not a category either; they go into
 *    their own per-site store (`task-distances.ts`), which the Tasks
 *    tab reads.
 *
 * 2. Fire alarm / alerts / trajectories — categories with no v1
 *    contract yet (not in the backend's `CategoryName` enum). These
 *    remain on the legacy `ManagedLive.subscribe()` compatibility
 *    path. (Beacons / dispensers / ingestors / RIOs used to sit here
 *    too — removed along with the Devices Systems panel, because
 *    nothing has ever published them on the v1 plane: no worker
 *    projection, no api-server publisher, and the SSE classifier
 *    never admitted them, so their handlers rejected every event.)
 *
 * 3. Receipts and feed health (twin slice 1, plan W3 step 5). Every
 *    raw robot event is noted in the site's receipt book
 *    (`robot-receipts.ts`) the moment it arrives -- not on the 500 ms
 *    flush, which sees only the newest record per robot per tick. The
 *    book is reset when the stream opens and when the page wakes (shown
 *    after 2 s or more hidden, or restored from the back-forward cache),
 *    so every robot reads "checking" again together. Counts only: a
 *    robot's age still runs from its last real receipt, so a stream
 *    that keeps reopening cannot keep a silent robot from going lost.
 *    After more than 15 s hidden the stream is also re-handshaken
 *    unless it is plainly alive (`restartIfIdle`). The site's
 *    `FeedHealth` is derived from the transport's own snapshot and
 *    published for the marks, the attention list and the lift columns.
 *    Each flush that carried robots or lifts also notes, in the site's
 *    `RideBook`, every robot riding a car: the one settling memory every
 *    presence surface reads.
 *
 * Both paths depend on the transport actually being connected.
 * Historically nothing in the app ever called `FetchSseTransport
 * .connect(site)` — `.subscribe()` only registers a listener, it
 * never opens the HTTP stream — so no live data reached the browser
 * at all regardless of any downstream parsing. This hook is now the
 * single owner of that connection lifecycle.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchSseTransport } from '../fetch-sse';
import { NO_FEED, deriveFeedHealth, getFeedHealth, publishFeedHealth } from '../feed-health';
import { robotReceiptsFor } from '../robot-receipts';
import { noteLiveTaskDistance } from '../task-distances';
import { rideBookFor } from '../../screens/Map/robot-presence';
import { monotonicNow } from '../../state/clock';
import { applyToRegistry, createSiteReducerRegistry } from '../category-reducers';
import {
  adaptFleetRegistry,
  adaptRobotState,
  adaptTaskSummary,
  adaptV1DoorState,
  adaptV1LiftState,
} from '../rmf-client';
import type { RobotStateView, TaskStateView } from '../rmf-client';
import type { DoorViewModel, LiftViewModel } from '../adapters';
import {
  doorsQueryOptions,
  fleetsQueryOptions,
  liftsQueryOptions,
  robotsQueryOptions,
  tasksQueryOptions,
} from '../query-options';
import { fetchLiftsRegistry } from '../v1-client';
import {
  alertRequestsSubscriptionKey,
  alertResponsesSubscriptionKey,
  trajectoryKey,
} from '../ws-types';
import type { TrajectorySnapshot } from '../ws-types';
import { getRuntimeConfig } from '../../config/runtime-config';
import { useSafeSiteId } from '../../state/useActiveSite';
import {
  ALERTS_KEY,
  BUILDING_MAP_KEY,
  FIRE_ALARM_KEY,
  TRAJECTORIES_KEY,
  TRAJECTORY_FEED_KEY,
  alertsKey,
  fireAlarmKey,
  trajectoriesKey,
  trajectoryFeedKey,
} from '../scope';
import type { TrajectoryFeedMapStatus, TrajectoryFeedStatus } from './useTrajectoryFeed';
import type { DoorState, FleetRegistry, LiftRegistry, LiftState, RobotState, SiteId, TaskSummary, V1LiveEvent } from '../v1';

type LiveCategory = 'fleets' | 'robots' | 'doors' | 'lifts' | 'tasks';
const LIVE_CATEGORIES: readonly LiveCategory[] = ['fleets', 'robots', 'doors', 'lifts', 'tasks'];

type AlertsPayload = Array<unknown>;

interface FireAlarmPayload {
  trigger?: boolean;
  unix_millis_time?: number;
}

/** Maximum display update rate in milliseconds (2 Hz). */
const DISPLAY_THROTTLE_MS = 500;

/** Hidden at least this long, the page has WOKEN: receipts reset. */
export const WAKE_RESET_HIDDEN_MS = 2_000;
/** Hidden longer than this, the stream is re-handshaken unless alive. */
export const WAKE_RESTART_HIDDEN_MS = 15_000;

/**
 * Merges freshly-adapted live entities into whatever the query cache
 * ALREADY holds (keyed by `getId`), instead of replacing the whole
 * array with just the reducer's own accumulated set.
 *
 * Replacing outright is only correct if `registry.<category>.state
 * .byId` is a complete, properly-seeded snapshot — but nothing on the
 * wire ever sends a `kind: 'snapshot'` event (confirmed:
 * `fetch-sse.ts` never constructs one; every live update classifies
 * as `kind: 'event'`), so each reducer starts EMPTY and only ever
 * accumulates entities that have individually had a live update since
 * THIS connection opened. Robots self-report often enough that the
 * gap was hard to notice; a task that reaches a terminal state stops
 * publishing entirely, so the very first live "tasks" event replaced
 * the ENTIRE cached list with just that one entry — confirmed live:
 * the Tasks screen's list collapsed to 0-1 rows the moment any task's
 * status changed, because every other task the REST fetch had seeded
 * was never in the reducer's own map to begin with.
 *
 * `retracted` is the one and only deletion signal. The merge stays
 * purely additive against absence — an entity missing from an
 * incremental update has simply not published lately, not gone — but
 * an id the reducer was EXPLICITLY told to drop (a tombstone naming
 * it, or a manifest that no longer lists it; see
 * `category-reducers.ts::ReducerState.retracted`) is removed here too.
 * Without this the merge could only ever grow: when the two orphaned
 * `patrol.dispatch-48` / `Chargee4d3cb` keys were cleared on
 * 2026-08-30, every console with the Tasks screen already open kept
 * rendering both rows — with a Cancel button that could never do
 * anything — until the operator was told to hard-reload.
 *
 * Note what this does NOT cover, so nobody reads more into it: a bare
 * zenoh DELETE of the key (what was actually run by hand on
 * 2026-08-30) arrives as a sample with no payload, which
 * `fetch-sse.ts::deriveV1LiveEvent` drops — the row still survives
 * until a reload. Retraction only flows from an explicit tombstone or
 * manifest.
 *
 * And be plain about the coverage, because the shape of this code
 * invites the wrong assumption: NOTHING emits a `tasks` tombstone or a
 * `tasks` manifest today. The worker's `publish_tombstone` call sites
 * are fleets/robots/doors/lifts only, and so are its manifests; the
 * admin retire endpoint does a plain put of a synthetic terminal
 * record on the same key, deliberately, because the history has to
 * survive. So the tasks half of this path is DEFENSIVE ONLY — it is
 * exercised by tests, not by production traffic — and the live
 * task-removal story is that synthetic record, which updates the row
 * in place with no reload. The robots/doors/lifts half is the half
 * under real traffic. Keep the tasks half anyway: it costs one set
 * lookup and it is what makes a future tombstone land correctly
 * instead of needing this bug found a second time.
 */
function mergeById<T>(
  previous: T[] | undefined,
  incoming: T[],
  getId: (item: T) => string,
  retracted?: ReadonlySet<string>,
): T[] {
  const byId = new Map((previous ?? []).map((item) => [getId(item), item]));
  for (const item of incoming) {
    byId.set(getId(item), item);
  }
  // After the merge, not before: the reducer clears an id from
  // `retracted` the moment it is re-asserted, so anything still listed
  // here is genuinely retracted and must not survive on a re-add.
  if (retracted) {
    for (const id of retracted) byId.delete(id);
  }
  return Array.from(byId.values());
}

/**
 * v1 `fleets`/`doors`/`lifts`/`robots`/`tasks` overlay — reducer-backed,
 * writes into the same `v1Key()` cache slots the REST seed hooks use.
 */
function useLiveCategoryData(siteId: string): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!siteId || !getRuntimeConfig().apiBaseUrl) return;
    const site = siteId as SiteId;
    const registry = createSiteReducerRegistry(site);
    const transport = fetchSseTransport();
    const receipts = robotReceiptsFor(site);
    const rides = rideBookFor(site);
    // A fresh watch of this site: nothing has been seen to advance yet,
    // and a robot never heard from is measured from now.
    receipts.beginWatch();

    // Set when the page wakes; cleared by the first data event after it.
    let wokeAtMs: number | null = null;
    const evaluateHealth = () => {
      const snapshot = transport.siteSnapshot(site);
      if (wokeAtMs !== null && snapshot?.lastEventAt != null && snapshot.lastEventAt > wokeAtMs) {
        wokeAtMs = null;
      }
      publishFeedHealth(site, deriveFeedHealth({ snapshot, wokeAtMs, nowMs: Date.now() }));
    };

    // Registered BEFORE `connect`, so the first 'open' cannot be missed.
    // The site argument is the transport's appended second one: another
    // site's stream reconnecting says nothing about this one.
    const offState = transport.onState((state, changedSite) => {
      if (changedSite !== site) return;
      // A (re)opened stream starts with a replay of retained records,
      // which look exactly like live ones: count from zero again (but
      // never forget when each robot was last heard; see the book).
      if (state === 'open') receipts.reset();
      evaluateHealth();
    });

    const wake = (restart: boolean) => {
      receipts.reset();
      wokeAtMs = Date.now();
      if (restart) transport.restartIfIdle(site);
      evaluateHealth();
    };
    // Both clocks, and the larger gap: `performance.now()` may stand
    // still while the device sleeps, and the wall clock can step. Either
    // one seeing a long gap is enough to distrust what is on screen.
    let hiddenAt: { wall: number; mono: number } | null = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = { wall: Date.now(), mono: monotonicNow() };
        return;
      }
      if (hiddenAt === null) return;
      const hiddenFor = Math.max(Date.now() - hiddenAt.wall, monotonicNow() - hiddenAt.mono);
      hiddenAt = null;
      if (hiddenFor >= WAKE_RESET_HIDDEN_MS) wake(hiddenFor > WAKE_RESTART_HIDDEN_MS);
    };
    // A page restored from the back-forward cache was frozen for an
    // unknowable time; `restartIfIdle` itself decides whether the
    // stream needs it.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) wake(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);

    transport.connect(site);
    evaluateHealth();

    // `adaptV1LiftState`'s `levels`/`ref`/`width`/`depth` come from
    // the `LiftRegistry` (static geometry, plan §Phase 4B/4D), not
    // the live `LiftState` payload — the flush handler below used to
    // call `adaptV1LiftState` with no registry argument at all,
    // silently dropping those fields on every single live lift
    // event and overwriting the correctly-merged REST-fetch result
    // (`rmf-client.ts::fetchLifts`) within moments of the initial
    // load — the destination-floor dropdown in `LiftDetailDrawer`
    // was empty in practice because live events arrive far faster
    // than a user can open the drawer. Registry geometry is
    // effectively static, so fetching it once per connection
    // lifecycle (not re-fetched per event) is the right cost/
    // freshness tradeoff.
    let liftRegistryByName = new Map<string, LiftRegistry>();
    fetchLiftsRegistry(site)
      .then((regs) => {
        liftRegistryByName = new Map(regs.map((r) => [r.id.lift, r]));
      })
      .catch(() => {
        // Leave the map empty; the drawer degrades to "—"/no floor
        // options rather than crashing (plan §fail gracefully).
      });

    const queryKeyFor = {
      fleets: fleetsQueryOptions(site).queryKey,
      robots: robotsQueryOptions(site).queryKey,
      doors: doorsQueryOptions(site).queryKey,
      lifts: liftsQueryOptions(site).queryKey,
      tasks: tasksQueryOptions(site).queryKey,
    } as const;

    const dirty = new Set<LiveCategory>();

    function requestResnapshot(category: LiveCategory): void {
      // Reset the reducer's local state so subsequent incremental
      // events rebuild cleanly against the freshly refetched
      // baseline rather than merging onto stale entries (plan §Live
      // consistency protocol: "replace... it must not merge
      // indefinitely").
      const handle = registry[category];
      handle.state.byId.clear();
      handle.state.ids = [];
      handle.state.epoch = null;
      handle.state.revision = null;
      // The refetched snapshot is the new truth about what exists; a
      // retraction recorded against the old epoch must not keep
      // deleting rows out of it.
      handle.state.retracted.clear();
      void qc.invalidateQueries({ queryKey: queryKeyFor[category] });
    }

    const offEvent = transport.onEvent((evt: V1LiveEvent) => {
      if (evt.envelope?.site !== site) return;

      // The receipt, on the raw event and before anything else can
      // drop it: see `robot-receipts.ts` on why not in the flush.
      if (evt.kind === 'event' && evt.category === 'robots') receipts.noteRobot(evt.payload);
      // A clamp a data event can lift (reconnecting with nothing on the
      // new response yet, waking, silent) lifts on that event, not up
      // to a flush later: every reopen would otherwise grey the screen
      // for as long again. A healthy stream skips this entirely.
      if (getFeedHealth(site).clamped) evaluateHealth();

      if (evt.kind === 'task_distance') {
        // A task's distance totals: into their own store, on every
        // console, whichever screen is up (see `task-distances.ts` for
        // why not the query cache). Never near the `tasks` reducer.
        noteLiveTaskDistance(site, evt.taskId, evt.payload);
        return;
      }

      // A task log line is the open drawer's business (`useTaskLog`),
      // and never the `tasks` reducer's: it carries no `id`.
      if (evt.kind === 'task_log') return;

      if (evt.kind === 'building_map_revision') {
        // Partial key (no level segment): TanStack Query's default
        // prefix match invalidates every cached level's snapshot,
        // not just whichever one happens to be active — the
        // notification doesn't carry the affected level, and
        // invalidating too broadly here is harmless (a refetch of an
        // inactive level is cheap and infrequent) where invalidating
        // too narrowly would leave a stale snapshot cached.
        void qc.invalidateQueries({ queryKey: [BUILDING_MAP_KEY, site] });
        return;
      }

      const category: LiveCategory | undefined =
        evt.kind === 'snapshot' || evt.kind === 'event'
          ? (evt.category as LiveCategory)
          : evt.kind === 'manifest' || evt.kind === 'tombstone'
            ? (evt.payload.category as LiveCategory)
            : undefined;
      if (!category || !LIVE_CATEGORIES.includes(category)) return;

      const result = applyToRegistry(registry, evt);
      if (result === 'needs_snapshot') {
        requestResnapshot(category);
        return;
      }
      if (result === 'applied') {
        dirty.add(category);
      }
    });

    const flush = window.setInterval(() => {
      // Cheap, and a no-op unless something changed: the transport's
      // idle and backoff transitions all arrive through `onState`, but
      // an open stream going `silent` is only the passing of time,
      // which only this sees.
      evaluateHealth();
      if (dirty.size === 0) return;
      for (const category of dirty) {
        // A future bad payload/adapter bug in one category must not
        // take every other category down with it, nor skip
        // `dirty.clear()` below (which would leave the SAME poisoned
        // entry re-thrown on every subsequent tick forever, appearing
        // as every category's live updates having silently stopped —
        // confirmed live, this is exactly what one misclassified
        // task-log entry did to `robots`/`doors`/`lifts` before the
        // classification bug itself was fixed).
        try {
          switch (category) {
            case 'fleets':
              qc.setQueryData(
                queryKeyFor.fleets,
                adaptFleetRegistry(Array.from(registry.fleets.state.byId.values()) as FleetRegistry[]),
              );
              break;
            case 'robots': {
              const incoming = (Array.from(registry.robots.state.byId.values()) as RobotState[]).map(adaptRobotState);
              const merged = mergeById(
                qc.getQueryData<RobotStateView[]>(queryKeyFor.robots),
                incoming,
                (r) => r.name,
                registry.robots.state.retracted,
              );
              qc.setQueryData(queryKeyFor.robots, merged);
              break;
            }
            case 'doors': {
              const incoming = (Array.from(registry.doors.state.byId.values()) as DoorState[]).map(adaptV1DoorState);
              qc.setQueryData(
                queryKeyFor.doors,
                mergeById(
                  qc.getQueryData<DoorViewModel[]>(queryKeyFor.doors),
                  incoming,
                  (d) => d.name,
                  registry.doors.state.retracted,
                ),
              );
              break;
            }
            case 'lifts': {
              const incoming = (Array.from(registry.lifts.state.byId.values()) as LiftState[]).map((l) =>
                adaptV1LiftState(l, liftRegistryByName.get(l.id.lift)),
              );
              qc.setQueryData(
                queryKeyFor.lifts,
                mergeById(
                  qc.getQueryData<LiftViewModel[]>(queryKeyFor.lifts),
                  incoming,
                  (l) => l.name,
                  registry.lifts.state.retracted,
                ),
              );
              break;
            }
            case 'tasks': {
              // Defense-in-depth: `deriveV1LiveEvent` (fetch-sse.ts)
              // is what should keep anything without a real
              // `TaskSummary` shape out of this reducer, but a single
              // malformed entry reaching `adaptTaskSummary` (which
              // dereferences `.id.task` unconditionally) would throw
              // and abort this entire flush tick before
              // `dirty.clear()` runs below — taking every OTHER
              // category's update down with it until a full reload,
              // not just tasks. Confirmed live: a misclassified
              // task-log entry did exactly this.
              // Entries, not values: the id is the key into
              // `receivedAt`, which carries the envelope timestamp of
              // the last publication for this task. Without it the
              // quiet badge would have to measure from the task's own
              // `started_at_ms` and would fire on every healthy job
              // running longer than the threshold.
              const incoming = (Array.from(registry.tasks.state.byId.entries()) as [string, TaskSummary][])
                .filter(([, t]) => Boolean(t?.id?.task))
                .map(([id, t]) => adaptTaskSummary(t, registry.tasks.state.receivedAt.get(id)));
              qc.setQueryData(
                queryKeyFor.tasks,
                mergeById(
                  qc.getQueryData<TaskStateView[]>(queryKeyFor.tasks),
                  incoming,
                  (t) => t.id,
                  registry.tasks.state.retracted,
                ),
              );
              break;
            }
          }
        } catch (err) {
          console.error(`[useLiveData] failed to flush category "${category}"`, err);
        }
      }
      // Rides, here where robots and lifts both flow, whatever screen is
      // open: a surface mounted after a ride reads the same settling
      // window as one that watched it (`RideBook`).
      if (dirty.has('robots') || dirty.has('lifts')) {
        try {
          rides.observe(
            qc.getQueryData<RobotStateView[]>(queryKeyFor.robots),
            qc.getQueryData<LiftViewModel[]>(queryKeyFor.lifts),
            receipts.now(),
          );
        } catch (err) {
          console.error('[useLiveData] failed to note rides', err);
        }
      }
      dirty.clear();
    }, DISPLAY_THROTTLE_MS);

    return () => {
      window.clearInterval(flush);
      offEvent();
      offState();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      transport.disconnect(site);
      // Nothing holds this site's stream any more; whatever was last
      // published must not go on reading as live.
      publishFeedHealth(site, NO_FEED);
    };
  }, [qc, siteId]);
}

/**
 * Legacy-shim overlay for categories with no v1 contract yet (fire
 * alarm, alerts, trajectories). The beacon/dispenser/ingestor/RIOS
 * legs that used to live beside these were removed with the Devices
 * Systems panel — they subscribed on the dead `rmf/sites/...` prefix
 * for categories nothing has ever produced.
 */
function useLegacyCategoryData(siteId: string): void {
  const qc = useQueryClient();

  const pendingRef = useRef<{
    alerts: AlertsPayload | null;
    fireAlarm: FireAlarmPayload | null;
    trajectories: Map<string, TrajectorySnapshot> | null;
    trajectoryFeed: Map<string, TrajectoryFeedMapStatus> | null;
  }>({
    alerts: null,
    fireAlarm: null,
    trajectories: null,
    trajectoryFeed: null,
  });
  const dirtyRef = useRef(false);

  useEffect(() => {
    pendingRef.current = {
      alerts: null,
      fireAlarm: null,
      trajectories: null,
      trajectoryFeed: null,
    };
    dirtyRef.current = false;
    qc.removeQueries({ queryKey: [ALERTS_KEY], exact: false });
    qc.removeQueries({ queryKey: [FIRE_ALARM_KEY], exact: false });
    qc.removeQueries({ queryKey: [TRAJECTORIES_KEY], exact: false });
    qc.removeQueries({ queryKey: [TRAJECTORY_FEED_KEY], exact: false });
  }, [qc, siteId]);

  useEffect(() => {
    const handle = window.setInterval(() => {
      if (!dirtyRef.current) return;
      const pending = pendingRef.current;
      dirtyRef.current = false;
      if (pending.alerts) {
        qc.setQueryData(alertsKey(siteId), pending.alerts);
        pending.alerts = null;
      }
      if (pending.fireAlarm) {
        qc.setQueryData(fireAlarmKey(siteId), pending.fireAlarm);
        pending.fireAlarm = null;
      }
      if (pending.trajectories) {
        for (const [map, snapshot] of pending.trajectories) {
          qc.setQueryData(trajectoriesKey(siteId, map), snapshot);
        }
        pending.trajectories = null;
      }
      if (pending.trajectoryFeed) {
        const feed = pending.trajectoryFeed;
        qc.setQueryData<TrajectoryFeedStatus>(trajectoryFeedKey(siteId), (prev) => ({
          ...(prev ?? {}),
          ...Object.fromEntries(feed),
        }));
        pending.trajectoryFeed = null;
      }
    }, DISPLAY_THROTTLE_MS);
    return () => window.clearInterval(handle);
  }, [qc, siteId]);

  useEffect(() => {
    const cfg = getRuntimeConfig();
    if (!cfg.apiBaseUrl || !siteId) return;

    const live = fetchSseTransport();

    const alertHandles = [
      live.subscribe<AlertsPayload>(alertRequestsSubscriptionKey(siteId), (env) => {
        const payload = env.payload;
        if (!Array.isArray(payload)) return;
        pendingRef.current.alerts = payload as AlertsPayload;
        dirtyRef.current = true;
      }),
      live.subscribe<AlertsPayload>(alertResponsesSubscriptionKey(siteId), (env) => {
        const payload = env.payload;
        if (!Array.isArray(payload)) return;
        pendingRef.current.alerts = payload as AlertsPayload;
        dirtyRef.current = true;
      }),
    ];

    // Deliberately `onEvent` and not `subscribe()`: `subscribe()` does
    // not filter by the pattern it is handed — it fans every
    // classified event out to every handler and leaves discrimination
    // to the handler's own payload check. For most categories a wrong
    // payload merely fails a shape test, but fire alarm has no
    // required field: `adaptFireAlarm` reads `Boolean(raw.trigger)`,
    // so ANY other event's payload adapts to `trigger: false` — a
    // robot state arriving after a real alarm would clear the overlay
    // and stop the siren mid-evacuation. Matching on the event kind is
    // what makes that impossible, so it must stay a kind check.
    const fireAlarmOff = live.onEvent((evt) => {
      if (evt.kind !== 'fire_alarm') return;
      if (evt.envelope?.site && evt.envelope.site !== siteId) return;
      if (!evt.payload) return;
      pendingRef.current.fireAlarm = evt.payload as FireAlarmPayload;
      dirtyRef.current = true;
    });
    const fireAlarmHandle = { unsubscribe: fireAlarmOff };

    // The pattern argument here is cosmetic only — `live.subscribe()`'s
    // underlying transport doesn't filter by it, it fans out every event
    // for the site and relies on the handler's own payload shape check
    // (matching the dispenser/ingestor handlers above). The real map name
    // comes from the TrajectorySnapshot payload's own `map_name` field,
    // not from `env.key` — `v1ToLegacyEnvelope` always echoes back the
    // *subscribed* pattern as `env.key`, never the real incoming key, so
    // parsing a map name out of it previously always yielded whatever
    // hardcoded placeholder was passed at subscribe time.
    const trajectoryKeyStr = trajectoryKey(siteId);
    const trajectoryHandle = live.subscribe<TrajectorySnapshot>(trajectoryKeyStr, (env) => {
      const payload = env.payload;
      if (!payload || typeof payload !== 'object') return;
      const p = payload as TrajectorySnapshot;
      const map = p.map_name;
      if (!map) return;
      // Publisher-side staleness (`TrajectorySnapshot.stale`). A stale
      // snapshot's `trajectories` describe an empty/old schedule mirror,
      // not the robots — writing it would blank the map's paths in a way
      // the UI cannot tell from "no itinerary". So: record the feed
      // status (surfaced by `useTrajectoryFeedStale` → top-bar chip) and
      // keep whatever non-stale snapshot the cache already holds. Older
      // publishers never send the field and take the normal path.
      const stale = p.stale === true;
      if (!pendingRef.current.trajectoryFeed) pendingRef.current.trajectoryFeed = new Map();
      pendingRef.current.trajectoryFeed.set(map, {
        stale,
        mirrorAgeMs: typeof p.mirror_age_ms === 'number' ? p.mirror_age_ms : null,
        receivedAt: Date.now(),
      });
      if (stale) {
        dirtyRef.current = true;
        return;
      }
      if (!pendingRef.current.trajectories) pendingRef.current.trajectories = new Map();
      pendingRef.current.trajectories.set(map, p);
      dirtyRef.current = true;
    });

    return () => {
      for (const h of alertHandles) h.unsubscribe();
      fireAlarmHandle.unsubscribe();
      trajectoryHandle.unsubscribe();
    };
  }, [qc, siteId]);
}

export function useLiveData(): void {
  const siteId = useSafeSiteId();
  useLiveCategoryData(siteId);
  useLegacyCategoryData(siteId);
}
